import { ErroHttp } from '@/shared/utils/erroHttp';

import type { EstadoServidorAutorizacaoAr } from './dto';

/**
 * Os `error.code` que a API de autorização de AR usa para separar causas distintas
 * sob o mesmo status HTTP. Tratá-los pelo texto da mensagem não serve: ela muda com
 * o corpo devolvido pelo backend, e cada código exige uma reação diferente.
 */
export type CodigoErroAutorizacaoAr =
  | 'SOLICITACAO_JA_DECIDIDA'
  | 'SOLICITACAO_SUPERADA'
  | 'COBRANCA_NAO_EMITIDA'
  | 'VERIFICACAO_EM_ANDAMENTO'
  | 'GATEWAY_INDISPONIVEL'
  | 'ACAO_INDISPONIVEL_NA_MODALIDADE'
  | 'PROCESSO_FORA_DE_CITACAO';

const CODIGOS: readonly string[] = [
  'SOLICITACAO_JA_DECIDIDA',
  'SOLICITACAO_SUPERADA',
  'COBRANCA_NAO_EMITIDA',
  'VERIFICACAO_EM_ANDAMENTO',
  'GATEWAY_INDISPONIVEL',
  'ACAO_INDISPONIVEL_NA_MODALIDADE',
  'PROCESSO_FORA_DE_CITACAO',
];

/**
 * Rejeição de uma decisão, como o slice e o hook a leem.
 *
 * O `rejectValue: string` anterior jogava fora o `code` que `ErroHttp` já carregava:
 * os seis motivos viravam o mesmo toast, e um `SOLICITACAO_JA_DECIDIDA` — que traz o
 * estado novo em `details` — disparava um refetch genérico em vez de aplicar o que o
 * servidor acabou de informar.
 */
export interface FalhaAutorizacaoAr {
  mensagem: string;
  codigo: CodigoErroAutorizacaoAr | null;
  /** Estado que o servidor diz valer agora; só vem em `SOLICITACAO_JA_DECIDIDA`. */
  estadoAtual: EstadoServidorAutorizacaoAr | null;
}

/**
 * `ErroHttp` com o `details` do corpo preservado. Estende em vez de duplicar: o
 * `status`, o `Retry-After` e o `codigo` continuam sendo os mesmos do resto do repo,
 * e só o `details` — que é específico desta API — entra aqui.
 */
export class ErroAutorizacaoAr extends ErroHttp {
  readonly detalhes: unknown;

  constructor(mensagem: string, status: number, codigo: string | null, detalhes: unknown) {
    super(mensagem, status, null, codigo);
    this.name = 'ErroAutorizacaoAr';
    this.detalhes = detalhes;
  }
}

const ESTADOS_DO_SERVIDOR: readonly string[] = [
  'AGUARDANDO_AUTORIZACAO',
  'PAGO',
  'AUTORIZADO_CONVENIO',
  'RECUSADO',
  'ENDERECO_EM_CORRECAO',
  'ERRO_EMISSAO',
];

function estadoEmDetalhes(detalhes: unknown): EstadoServidorAutorizacaoAr | null {
  if (typeof detalhes !== 'object' || detalhes === null) return null;
  const estado = (detalhes as Record<string, unknown>).estado;
  return typeof estado === 'string' && ESTADOS_DO_SERVIDOR.includes(estado)
    ? (estado as EstadoServidorAutorizacaoAr)
    : null;
}

export function comoFalhaDaDecisao(erro: unknown, mensagemPadrao: string): FalhaAutorizacaoAr {
  if (!(erro instanceof ErroHttp)) {
    return {
      mensagem: erro instanceof Error && erro.message ? erro.message : mensagemPadrao,
      codigo: null,
      estadoAtual: null,
    };
  }
  const codigo =
    erro.codigo && CODIGOS.includes(erro.codigo) ? (erro.codigo as CodigoErroAutorizacaoAr) : null;
  return {
    mensagem: erro.message || mensagemPadrao,
    codigo,
    estadoAtual:
      codigo === 'SOLICITACAO_JA_DECIDIDA' && erro instanceof ErroAutorizacaoAr
        ? estadoEmDetalhes(erro.detalhes)
        : null,
  };
}

/**
 * O que dizer ao requerente para cada código. Mora junto do tipo porque a razão de
 * mudar é a mesma: um código novo na API precisa de uma frase nova, e deixá-lo cair
 * no genérico é como os seis viraram o mesmo toast.
 */
export const TITULO_POR_CODIGO: Record<CodigoErroAutorizacaoAr, string> = {
  SOLICITACAO_JA_DECIDIDA: 'Esta solicitação já foi respondida',
  SOLICITACAO_SUPERADA: 'Esta solicitação foi substituída',
  COBRANCA_NAO_EMITIDA: 'A cobrança ainda não foi emitida',
  VERIFICACAO_EM_ANDAMENTO: 'Já estamos verificando',
  GATEWAY_INDISPONIVEL: 'O gateway de pagamento não respondeu',
  ACAO_INDISPONIVEL_NA_MODALIDADE: 'Esta ação não vale para este envio',
  PROCESSO_FORA_DE_CITACAO: 'Este processo saiu da fase de citação',
};

export const DETALHE_POR_CODIGO: Record<CodigoErroAutorizacaoAr, string> = {
  SOLICITACAO_JA_DECIDIDA: 'Atualizamos o card com a decisão que já está registrada.',
  SOLICITACAO_SUPERADA:
    'A secretaria enviou uma solicitação mais recente para este requerido. Estamos recarregando a lista.',
  COBRANCA_NAO_EMITIDA:
    'Estamos preparando a cobrança deste envio. Tente de novo em alguns instantes.',
  VERIFICACAO_EM_ANDAMENTO: 'A consulta anterior ainda está em curso — aguarde o resultado.',
  GATEWAY_INDISPONIVEL: 'Nada foi cobrado. Tente novamente em alguns minutos.',
  ACAO_INDISPONIVEL_NA_MODALIDADE:
    'Recarregamos a lista para mostrar as opções que valem para ele.',
  // Sai quando o processo deixa `EM_CITACAO` entre a leitura e o clique: o servidor
  // passa a recusar decisão sobre envio de AR, e a secretaria é quem destrava. Não é
  // erro do requerente, por isso o texto manda falar com ela em vez de tentar de novo.
  PROCESSO_FORA_DE_CITACAO:
    'A citação deste processo não está mais em curso. Fale com a secretaria para saber o próximo passo.',
};
